import { FormConfigField, MEDIA_FIELD_TYPES, WorkspaceTask } from "../shared/types";

// ----------------------------  Raw value helpers  ----------------------------

type RawValues = Record<string, unknown>;

const isMedia = (f: FormConfigField): boolean =>
  (MEDIA_FIELD_TYPES as readonly string[]).indexOf(f.type) !== -1;

const toText = (v: unknown): string | undefined => {
  if (v === null || v === undefined) return undefined;
  if (Array.isArray(v)) return v.length ? v.map((x) => String(x)).join(", ") : undefined;
  const s = String(v).trim();
  return s ? s : undefined;
};

const norm = (s: string): string => s.trim().toLowerCase();

// Match a raw value to one of the field's options, returning the option's
// canonical casing (e.g. "e-waste" -> "E-waste").
const matchOption = (f: FormConfigField, value: string | undefined): string | undefined => {
  if (!value) return undefined;
  const hit = (f.options || []).find((o) => norm(o) === norm(value));
  return hit;
};

// ----------------------------  Field -> raw key heuristics  ----------------------------

type Source = "ai" | "human";

const resolveField = (f: FormConfigField, raw: RawValues, source: Source): string | undefined => {
  const label = norm(f.label);
  const verdict = toText(source === "ai" ? raw.predictedVerdict : raw.humanOverrideVerdict);
  const reasoning = toText(source === "ai" ? raw.predictedReasoning : raw.humanOverrideReasoning);
  const labelType = toText(raw.labelType);

  if (label.includes("verdict") || label.includes("compliance")) return verdict;
  if (label.includes("reason")) return reasoning;
  if (label.includes("confidence")) return source === "ai" ? toText(raw.confidence) : undefined;

  // Only the model predicts a class; humans override verdict/reasoning only.
  if (source === "ai") {
    const optionHit = matchOption(f, labelType);
    if (optionHit) return optionHit;
    if (/(class|type|category|label)/.test(label) && labelType) return labelType;
    // A dropdown whose options contain the verdict (e.g. Compliant/Non-Compliant).
    const verdictHit = matchOption(f, verdict);
    if (verdictHit) return verdictHit;
  } else {
    const verdictHit = matchOption(f, verdict);
    if (verdictHit) return verdictHit;
  }
  return undefined;
};

const fromFieldValues = (raw: RawValues): Record<string, string> => {
  const out: Record<string, string> = {};
  const list = Array.isArray(raw.fieldValues) ? raw.fieldValues : [];
  list.forEach((item) => {
    if (!item || typeof item !== "object") return;
    const { fieldName, llmValue } = item as { fieldName?: unknown; llmValue?: unknown };
    const v = toText(llmValue);
    if (typeof fieldName === "string" && v !== undefined) out[fieldName] = v;
  });
  return out;
};

// ----------------------------  Public API  ----------------------------

/**
 * Model ("LLM extracted") value per field label.
 * Priority: raw_values.fieldValues -> raw_values heuristics.
 */
export const buildAiValues = (
  task: WorkspaceTask,
  fields: FormConfigField[],
): Record<string, string> => {
  const raw: RawValues = task.raw_values || {};
  const listed = fromFieldValues(raw);
  const out: Record<string, string> = {};
  fields
    .filter((f) => !isMedia(f))
    .forEach((f) => {
      const v = listed[f.label] ?? resolveField(f, raw, "ai");
      if (v !== undefined) out[f.label] = v;
    });
  return out;
};

/**
 * Human override value per field label (only when an override exists).
 * Uses raw_values.humanOverride* when humanOverridePresent is true.
 */
export const buildHumanValues = (
  task: WorkspaceTask,
  fields: FormConfigField[],
): Record<string, string> => {
  const raw: RawValues = task.raw_values || {};
  const present = raw.humanOverridePresent === true;
  const out: Record<string, string> = {};
  fields
    .filter((f) => !isMedia(f))
    .forEach((f) => {
      const v = present ? resolveField(f, raw, "human") : undefined;
      if (v !== undefined) out[f.label] = v;
    });
  return out;
};
